import { useSelector } from 'react-redux';
import {
  selectAccount,
  selectProfile,
  selectSettings,
} from '../../store/auth/auth.selector';
import { Title } from '../../components/shared';
import moment from 'moment';
import { selectFarmsData } from '../../store/farms/farms.selector';
import FarmsOverviewChart from './FarmsOverviewChart';
import PerformanceCharts from './PerformanceCharts';
import CalendarView from './CalendarView';
import MessagesView from './MessagesView';
import UpcomingHarvests from './UpcomingHarvests';
import { useEffect, useState } from 'react';
import { IFarmWeatherResource } from '../../entities/farms.entities';
import { sendSingleRequest } from '../../apis';
import { formatNumber } from '../../entities/util-functions';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import SalinityChart from './SalinityChart';
import 'chart.js/auto';

const oneKnot = 1.94384;

const formatLocalDate = (lang: string | undefined): string => {
  const now = new Date();
  const locale = lang === 'es' ? 'es' : lang === 'fr' ? 'fr' : 'en';
  const day = now.getDate();
  const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(now);
  const year = now.getFullYear();
  if (lang === 'es') return `${day} de ${month} de ${year}`;
  if (lang === 'fr') return `${day} ${month} ${year}`;
  return `${day} of ${month} ${year}`;
};

interface IWeather {
  temperature: number;
  wind: number;
}

const HomePage = () => {
  const profile = useSelector(selectProfile);
  const farmsData = useSelector(selectFarmsData);
  const lang = useSelector(selectLang);
  const hasEagleIOKey = !!useSelector(selectAccount)?.eagle_io_api_key;
  const showHomeChats = !useSelector(selectSettings).hide_home_chats;

  const [weather, setWeather] = useState<IWeather>();

  useEffect(() => {
    (async () => {
      const min_date = moment().startOf('day').toDate().getTime();
      const max_date = moment().endOf('day').toDate().getTime();
      const farm = farmsData.find(f => f.bounds && f.bounds.length > 0);
      if (!farm) return;

      const response = await sendSingleRequest(
        { min_date, max_date, farm_id: farm.id },
        'GET',
        'api/farm/weather',
        true,
      );
      if (!response.status || response.data.length <= 0) return;
      const data: IFarmWeatherResource[] = response.data;
      let temp = 0,
        wind_u = 0,
        wind_v = 0;
      for (let d of data) {
        temp += Number(d.temperature);
        wind_u += Number(d.wind_u);
        wind_v += Number(d.wind_v);
      }
      temp /= data.length;
      wind_u /= data.length;
      wind_v /= data.length;
      const wind = Math.sqrt(wind_u * wind_u + wind_v * wind_v);
      setWeather({ temperature: temp, wind: wind * oneKnot });
    })();
  }, [farmsData]);

  return (
    <div className='bg-secondary min-h-100'>
      <div className='container'>
        <div className='mb-32 pt-24'>
          <Title size={5} color='black' align='default' fontWeight={600}>
            {`${translate(
              lang,
              '_hello_today',
              profile?.name,
              formatLocalDate(lang),
            )}${
              weather
                ? `, ${translate(
                    lang,
                    '_noon_wind_knots',
                    formatNumber(weather.temperature),
                    formatNumber(weather.wind),
                  )}`
                : ''
            }`}
          </Title>
        </div>
        <div className='home-body'>
          <div className='mr-24' style={{ flex: 2 }}>
            <FarmsOverviewChart farms={farmsData} />
            <PerformanceCharts />
          </div>
          <div className='ml-24' style={{ flex: 3 }}>
            <CalendarView />
            {showHomeChats && <MessagesView />}
            <UpcomingHarvests farms={farmsData} />
            {hasEagleIOKey && <SalinityChart />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
